import React, { useState, useEffect } from 'react';
import moment from 'moment';

//MUI
import { Box, Card, Grid, IconButton } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { Edit, Delete as DeleteIcon } from '@mui/icons-material';

import Swal from 'sweetalert2';

//COMPONENTS
import CustomTable from '@components/Tables/CustomTable';
import FormBanners from '@components/ppm/FormBanners';

//SERVICES
import ppmServices from '@services/PpmServices';

//MIDDLEWARE
import middleware from '@middlewares/middleware';

//UTILS
import { convertBase64 } from '@utils/Utilities';

const TableBanners = (props) => {
    const { setIsLoading, refresh, apiServiceId } = props;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [page] = useState(0);
    const [pageSize] = useState(10);
    const [openModal, setOpenModal] = useState(false);
    const [params, setParams] = useState({});

    const baseColumns = [
        { id: 'FechaCreo', label: 'Fecha Creación', align: 'left' },
        { id: 'title', label: 'Título', align: 'left' },
        { id: 'description', label: 'Descripción', align: 'left' },
        { id: 'image', label: 'Imagen', align: 'left', sort: false },
        { id: 'Accion', label: '', align: 'center' }
        // ...(hasEditPermission ? [{ id: 'Accion', label: '', align: 'center' }] : []),
    ];

    const WhiteTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} classes={{ popper: className }} />
    ))({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: '#fff',
            color: '#000',
            boxShadow: '0px 0px 10px rgba(0,0,0,0.1)',
            fontSize: 14,
        },
    });

    const mapData = (data) =>
        data.map((item, index) => ({
            // ...(hasEditPermission
            //     ? {
            //         Accion: (
            //             <IconButton onClick={() => handleEditClick(item)} sx={{ m: 0, p: 0 }} color="primaryDark">
            //                 <Edit />
            //             </IconButton>
            //         ),
            //     }
            //     : {}),
            Accion: (
                <Box sx={{ display: 'flex', gap: 3 }}>
                    <IconButton onClick={() => handleEditClick(item)} sx={{ m: 0, p: 0 }} color="primaryDark">
                        <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(item)} sx={{ m: 0, p: 0 }} color="error">
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ),
            image: (
                <WhiteTooltip
                    title={<img src={item.imagePath} alt={item.imageName} style={{ maxWidth: 200, maxHeight: 200 }} />}
                    placement="right"
                >
                    <a href={item.imagePath} target="_blank" rel="noopener noreferrer">
                        {item.imageName}
                    </a>
                </WhiteTooltip>
            ),
            ...item,
        }));

    const getData = async () => {
        try {
            setLoading(true);
            const result = await ppmServices.getBanners(apiServiceId ? { foca: true } : {});
            const { results, response, message } = result;
            if (results) {

                const formattedData = response.data.map((item) => ({
                    ...item,
                    FechaCreo: item.FechaCreo ? moment(item.FechaCreo).format('DD/MM/YYYY - HH:mm:ss') : null
                }));

                setData(mapData(formattedData));
            } else {
                throw new Error(message);
            }
        } catch (error) {
            Swal.fire({
                title: error.message,
                icon: 'warning',
                customClass: {
                    container: 'modal-alert',
                },
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getData();
        //eslint-disable-next-line
    }, [refresh]);


    const handleEditClick = (value) => {
        setParams(value);
        setOpenModal(true);
    };

    const handleUpdateData = async (value) => {
        try {
            setIsLoading(true);
            const file = value.files[0]?.file;
            if (file) {
                if (value.comparar === file) {
                    ['comparar', 'files'].forEach((prop) => {
                        if (value.hasOwnProperty(prop)) {
                            delete value[prop];
                        }
                    });
                } else {
                    const base64File = await convertBase64(file);
                    value.files[0].file = base64File;
                }
            }
            ['imageName', 'comparar'].forEach((prop) => {
                if (value.hasOwnProperty(prop)) {
                    delete value[prop];
                }
            });

            const result = await ppmServices.updateBanner({
                ...value,
                ...(apiServiceId ? { foca: true } : {})
            });

            const { results, message } = result;
            if (results) {
                await getData();
                Swal.fire({
                    title: message,
                    icon: 'success',
                    customClass: {
                        container: 'modal-alert',
                    },
                });
                setOpenModal(false);
            } else {
                throw new Error(message);
            }
        } catch (error) {
            Swal.fire({
                title: error.message,
                icon: 'warning',
                customClass: {
                    container: 'modal-alert',
                },
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (banner) => {
        // Llamar al modal de confirmación
        const result = await Swal.fire({
            title: '¿Estás seguro de eliminarlo?',
            text: "Esta acción no se puede deshacer.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            reverseButtons: true,
        });

        if (result.isConfirmed) {
            try {
                setIsLoading(true);
                const deleteResult = await ppmServices.deleteBanner({
                    id: banner?.id,
                    ...(apiServiceId ? { foca: true } : {})
                });
                const { success, message } = deleteResult;

                if (success) {
                    Swal.fire({
                        title: message,
                        icon: 'success',
                        customClass: { container: 'modal-alert' },
                    });

                    await getData();
                } else {
                    throw new Error(message);
                }
            } catch (e) {
                Swal.fire({
                    title: e.message,
                    icon: 'warning',
                    customClass: { container: 'modal-alert' },
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <Box component={Card} variant="outlined" borderRadius={2} padding={2} minHeight={100} elevation={0}>
            <Grid container>
                <Grid item xs={12}>
                    <CustomTable
                        rows={data}
                        columns={baseColumns}
                        total={data.length}
                        page={page}
                        pageSize={pageSize}
                        stickyHeader={true}
                        isLoading={loading}
                        maxHeight={600}
                        disableCardType
                    />
                </Grid>
            </Grid>
            {openModal && (
                <FormBanners
                    setModalOpen={setOpenModal}
                    modalOpen={openModal}
                    params={params}
                    setData={handleUpdateData}
                    showInut={false}
                />
            )}
        </Box>
    );
};

export default TableBanners;
